import { getNativeRefs } from '@/core/nativeEnvironment';

function nativeExec(regex: RegExp, target: string): RegExpExecArray | null {
  return getNativeRefs().execRegExp(regex, target);
}

function nativeReplace(
  target: string,
  searchValue: RegExp | string,
  replaceValue: string | ((...args: unknown[]) => string)
): string {
  return getNativeRefs().stringReplace(target, searchValue, replaceValue);
}

function nativeSplit(target: string, separator: RegExp | string): string[] {
  return getNativeRefs().stringSplit(target, separator);
}

function nativeTrim(value: string): string {
  return getNativeRefs().stringTrim(value);
}

function nativeEntries(value: Record<string, unknown>): Array<[string, unknown]> {
  return getNativeRefs().objectEntries(value);
}

function nativeKeys(value: unknown): string[] {
  return getNativeRefs().objectKeys(value);
}

function splitPath(path: string): string[] {
  return (path.match(/[^.[\]]+/g) ?? []).map(segment => nativeReplace(segment, /^['"]|['"]$/g, ''));
}

export function propertyTraverser(path: string) {
  const steps = splitPath(path);
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  return (object) => steps.reduce((obj, key) => {
    if (obj == null) {
      return undefined;
    }
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    return obj[key];
  }, object);
}

function hasPath(source: unknown, path: string): boolean {
  if (source == null) {
    return false;
  }
  
  const steps = splitPath(path);
  let current: unknown = source;
  
  for (const step of steps) {
    if (current == null || (typeof current !== 'object' && typeof current !== 'function')) {
      return false;
    }
    
    if (!Object.prototype.hasOwnProperty.call(current, step)) {
      return false;
    }
    
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    current = current[step];
  }
  
  return true;
}

// Pobierz wartość właściwości na podstawie tokenu
export function getter(token: string) {
  const isOptional = token.endsWith('?%');
  const getValue = propertyTraverser(nativeReplace(token, /%|\?/g, ''));
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  return data => {
    const value = getValue(data);
    if (value != null) {
      return value;
    }
    if (isOptional) {
      return '';
    }
  };
}

function escapeRegExp(value: string): string {
  return nativeReplace(value, /[.*+?^${}()|[\]\\]/g, '\\$&');
}

function normalizeValue(value: unknown): string {
  if (value == null) {
    return '';
  }
  
  if (Array.isArray(value)) {
    return value.map(item => normalizeValue(item)).filter(Boolean).join(', ');
  }
  
  if (typeof value === 'object') {
    return '';
  }
  
  return String(value);
}

function replaceToken(template: string, token: string, value: unknown): string {
  const escapedToken = escapeRegExp(token);
  const pattern = new RegExp(`{{{\\s*${escapedToken}\\s*}}}|{{\\s*${escapedToken}\\s*}}`, 'g');
  return nativeReplace(template, pattern, normalizeValue(value));
}

function toNumber(value: unknown): number | null {
  if (typeof value === 'number' && !Number.isNaN(value)) {
    return value;
  }

  if (typeof value === 'string') {
    const trimmed = nativeTrim(value);
    if (trimmed === '') {
      return null;
    }
    const parsed = Number(trimmed);
    return Number.isNaN(parsed) ? null : parsed;
  }
  
  if (typeof value === 'boolean') {
    return value ? 1 : 0;
  }
  
  return null;
}

function parseCompareValue(rawValue?: string): unknown {
  if (rawValue == null) {
    return null;
  }
  const trimmed = nativeTrim(rawValue);
  if (trimmed === '') {
    return '';
  }
  const numeric = toNumber(trimmed);
  if (numeric != null) {
    return numeric;
  }
  if (/^(true|false)$/i.test(trimmed)) {
    return trimmed.toLowerCase() === 'true';
  }
  return trimmed;
}

function evaluateCondition(value: unknown, operator: string | undefined, compareValue: unknown): boolean {
  if (!operator) {
    return Boolean(value);
  }
  
  const leftNumber = toNumber(value);
  const rightNumber = toNumber(compareValue);
  const bothNumeric = leftNumber != null && rightNumber != null;
  
  const leftString = value != null ? String(value) : '';
  const rightString = compareValue != null ? String(compareValue) : '';
  
  switch (operator) {
    case '===':
      if (bothNumeric) {
        return leftNumber === rightNumber;
      }
      return leftString === rightString;
    case '!==':
      if (bothNumeric) {
        return leftNumber !== rightNumber;
      }
      return leftString !== rightString;
    case '==':
      // eslint-disable-next-line eqeqeq
      return value == compareValue;
    case '!=':
      // eslint-disable-next-line eqeqeq
      return value != compareValue;
    case '>':
      if (bothNumeric) {
        return leftNumber > rightNumber;
      }
      return leftString > rightString;
    case '>=':
      if (bothNumeric) {
        return leftNumber >= rightNumber;
      }
      return leftString >= rightString;
    case '<':
      if (bothNumeric) {
        return leftNumber < rightNumber;
      }
      return leftString < rightString;
    case '<=':
      if (bothNumeric) {
        return leftNumber <= rightNumber;
      }
      return leftString <= rightString;
    default:
      return Boolean(value);
  }
}

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
function replaceConditionMacros (generatedHtml, mainProduct) {
  const ifRegex = /{{\s*#if\s+([^\s{}]+)\s*(===|!==|==|!=|>|>=|<|<=)?\s*(?:(['"])(.*?)\2|([^\s{}]+))?\s*}}([\s\S]+?){{\s*\/if\s*}}/gi;

  let newHtml = generatedHtml;
  const matches: RegExpExecArray[] = [];
  let currentMatch: RegExpExecArray | null;

  while ((currentMatch = nativeExec(ifRegex, newHtml)) !== null) {
    matches.push(currentMatch);
    if (currentMatch.index === ifRegex.lastIndex) {
      ifRegex.lastIndex += 1;
    }
  }

  matches.forEach(match => {
    const fullMatch = match[0];
    const rawPath = match[1];
    const operator = match[2];
    const quotedCompare = match[3];
    const unquotedCompare = match[4] ?? match[5];
    const fullIfContent = match[6];

    const targetPath = rawPath ? nativeTrim(rawPath) : undefined;

    if (!targetPath) {
      newHtml = nativeReplace(newHtml, fullMatch, '');
      return;
    }

    const splitContent = nativeSplit(fullIfContent, /{{\s*else\s*}}/i);
    const ifContent = splitContent[0] ?? '';
    const elseContent = splitContent[1] ?? '';

    const compareValue = parseCompareValue(quotedCompare ?? unquotedCompare);
    const valueResolver = propertyTraverser(targetPath);
    const value = valueResolver(mainProduct);

    const conditionMet = evaluateCondition(value, operator, compareValue);
    const replacement = conditionMet ? ifContent : elseContent;

    newHtml = nativeReplace(newHtml, fullMatch, replacement);
  });

  return newHtml;
}
function renderVariantTemplate(template: string, variant: Record<string, unknown>, lastValues: Map<string, string>): string {
  if (!template) {
    return '';
  }
  
  if (variant == null || typeof variant !== 'object') {
    return template;
  }
  
  let rendered = template;
  nativeEntries(variant).forEach(([key, rawValue]) => {
    const value = normalizeValue(rawValue);
    const previousValue = lastValues.get(key);
    
    if (value === previousValue) {
      rendered = replaceToken(rendered, key, '');
    } else {
      rendered = replaceToken(rendered, key, value);
      lastValues.set(key, value);
    }
  });
  
  return rendered;
}

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
function replaceLoopMacros(generatedHtml: string, productVariants: any[], showLimit?: number) {
  const loopRegex = /{{\s*#each\s+(\w+)\s*}}([\s\S]+?){{\s*\/each\s*}}/g;
  
  if (!Array.isArray(productVariants) || productVariants.length === 0) {
    return nativeReplace(generatedHtml, loopRegex, '');
  }
  
  return nativeReplace(generatedHtml, loopRegex, (_fullMatch, loopVariable, loopContent) => {
    if (loopVariable !== 'variant') {
      return '';
    }
    
    const showRegex = /{{\s*#show\s+(\d+)\s+elements\s*}}([\s\S]+?){{\s*showRest\s*}}([\s\S]+?){{\s*\/show\s*}}/i;
    
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
    const showMatch = nativeExec(showRegex, loopContent);
    const showLimitFromContent = showMatch ? parseInt(showMatch[1], 10) : showLimit;
    const firstTemplate = showMatch ? showMatch[2] : loopContent;
    const secondTemplate = showMatch ? showMatch[3] : '';
    
    if (!firstTemplate) {
      return '';
    }
    
    const lastPropertyValues = new Map<string, string>();
    const renderedChunks: string[] = [];
    
    productVariants.forEach((variant, index) => {
      if (showLimitFromContent != null && index >= showLimitFromContent) {
        return;
      }
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
      const rendered = renderVariantTemplate(firstTemplate, variant, lastPropertyValues);
      renderedChunks.push(rendered);
    });
    
    let loopResult = renderedChunks.join('');
    
    if (showMatch && showLimitFromContent != null && productVariants.length > showLimitFromContent) {
      let restTemplate = secondTemplate;
      
      productVariants.forEach((variant, index) => {
        if (index < showLimitFromContent) {
          return;
        }
        restTemplate = renderVariantTemplate(restTemplate, variant, lastPropertyValues);
      });
      
      loopResult += restTemplate;
    }
    
    return loopResult;
  });
}

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
function replaceMainMacros(generatedHtml, mainProduct) {
  if (mainProduct == null || typeof mainProduct !== 'object') {
    return generatedHtml;
  }
  
  nativeEntries(mainProduct).forEach(([key, value]) => {
    generatedHtml = replaceToken(generatedHtml, key, value);
  });
  
  return generatedHtml;
}

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
const HIDDEN_WHITESPACE_REGEX = /\u00a0|\u1680|\u180e|\u2000|\u2001|\u2002|\u2003|\u2004|\u2005|\u2006|\u2007|\u2008|\u2009|\u200a|\u202f|\u205f|\u3000/g;
const ZERO_WIDTH_REGEX = /\u200b|\u200c|\u200d|\ufeff/g;

function normalizeTemplate(template: string): string {
  let normalized = nativeReplace(template, /\r\n?/g, '\n');
  normalized = nativeReplace(normalized, HIDDEN_WHITESPACE_REGEX, ' ');
  return nativeReplace(normalized, ZERO_WIDTH_REGEX, '');
}

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
export function createTemplate(templateString, mainProduct, productVariants) {
  const normalizedTemplate = normalizeTemplate(templateString);
  let generatedHtml = normalizedTemplate;
  
  generatedHtml = replaceConditionMacros(generatedHtml, mainProduct);
  generatedHtml = replaceLoopMacros(generatedHtml, productVariants);
  generatedHtml = replaceMainMacros(generatedHtml, mainProduct);
  
  return sanitizeResidualTokens(generatedHtml);
}

function sanitizeResidualTokens(html: string): string {
  const fallbackIfRegex = /{{\s*#if\s+([^{}]+)}}([\s\S]*?){{\s*else\s*}}([\s\S]*?){{\s*\/if\s*}}/gi;
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
  html = nativeReplace(html, fallbackIfRegex, (_match: string, condition: string, ifPart: string, elsePart: string): string => {
    return elsePart;
  });
  
  const fallbackIfNoElseRegex = /{{\s*#if\s+([^{}]+)}}([\s\S]*?){{\s*\/if\s*}}/gi;
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
  html = nativeReplace(html, fallbackIfNoElseRegex, (_match: string, condition: string, ifPart: string): string => {
    return ifPart;
  });
  
  const residualTokenRegex = /{{{?\s*\/?(?:#|\/)?[^{}]+\s*}?}}/g;
  const residuals = html.match(residualTokenRegex);
  if (residuals && residuals.length > 0) {
    html = nativeReplace(html, residualTokenRegex, '');
  }
  return html;
}

export interface TemplateValidationIssue {
  type: 'unmatched-block' | 'unknown-token';
  token: string;
  message: string;
}

export interface TemplateValidationContext {
  mainProduct?: Record<string, unknown>;
  productVariants?: Array<Record<string, unknown>>;
  additionalTokens?: string[];
}

export function validateTemplate(templateString: string, context: TemplateValidationContext = {}): TemplateValidationIssue[] {
  const issues: TemplateValidationIssue[] = [];
  const blockStack: Array<{ type: 'if' | 'each' | 'show'; token: string }> = [];
  const {mainProduct = {}, productVariants = [], additionalTokens = []} = context;
  
  const blockRegex = /{{\s*(#|\/)(\w+)([^}]*)}}/gi;
  let blockMatch: RegExpExecArray | null;
  
  while ((blockMatch = nativeExec(blockRegex, templateString)) !== null) {
    const [token, mode, name] = blockMatch;
    const lowerName = name.toLowerCase();
    
    if (mode === '#') {
      if (lowerName === 'if' || lowerName === 'each' || lowerName === 'show') {
        blockStack.push({type: lowerName as 'if' | 'each' | 'show', token});
      }
    } else if (mode === '/') {
      if (blockStack.length === 0) {
        issues.push({
          type: 'unmatched-block',
          token,
          message: `Nieoczekiwane zamknięcie bloku ${token}`
        });
      } else {
        blockStack.pop();
      }
    }
  }
  
  blockStack.forEach(unclosed => {
    issues.push({
      type: 'unmatched-block',
      token: unclosed.token,
      message: `Brak zamknięcia dla bloku ${unclosed.token}`
    });
  });
  
  const tokenRegex = /{{{?\s*(?!#|\/|else\b)([^{}]+?)\s*}?}}/gi;
  let tokenMatch: RegExpExecArray | null;
  const seenTokens = new Set<string>();
  
  const knownTokens = new Set<string>([
    ...additionalTokens
  ]);
  
  const registerKnownTokens = (source: Record<string, unknown>) => {
    nativeKeys(source).forEach(key => knownTokens.add(key));
  };
  
  if (mainProduct && typeof mainProduct === 'object') {
    registerKnownTokens(mainProduct);
  }
  
  productVariants.forEach(variant => {
    if (variant && typeof variant === 'object') {
      registerKnownTokens(variant);
    }
  });
  
  while ((tokenMatch = nativeExec(tokenRegex, templateString)) !== null) {
    const token = nativeTrim(tokenMatch[1]);
    if (!token) {
      continue;
    }
    if (seenTokens.has(token)) {
      continue;
    }
    seenTokens.add(token);
    
    if (token.toLowerCase() === 'showrest') {
      continue;
    }
    
    if (token.startsWith('has') && token.length > 3) {
      const propertyCandidate = token.charAt(3).toLowerCase() + token.slice(4);
      if (
        hasPath(mainProduct, propertyCandidate) ||
        productVariants.some(variant => hasPath(variant, propertyCandidate))
      ) {
        continue;
      }
    }
    
    if (knownTokens.has(token)) {
      continue;
    }
    
    const existsInMain = hasPath(mainProduct, token);
    const existsInVariants = productVariants.some(variant => hasPath(variant, token));
    
    if (!existsInMain && !existsInVariants) {
      issues.push({
        type: 'unknown-token',
        token,
        message: `Makro {{${token}}} nie zostało znalezione w danych`
      });
    }
  }
  
  return issues;
}

/**
 * Przykłady użycia
 * {{#if someVariable}}
 *   <p>Zmienna someVariable istnieje!</p>
 * {{/if}}
 *
 *
 * {{#if someVariable}}
 *   <p>Zmienna someVariable istnieje!</p>
 *   {{else}}
 *   <p>Zmienna someVariable nie istnieje!</p>
 * {{/if}}
 *
 *
 */




